import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useChannels } from '../hooks/useChannels';
import RatingSelector from '../components/channels/RatingSelector';

export default function ChannelManagementPage() {
  const { channels, loading, error, addChannel, updateChannel, deleteChannel } = useChannels();

  const [channelInput, setChannelInput] = useState('');
  const [rating, setRating] = useState(3);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleAddChannel = async () => {
    if (!channelInput.trim()) {
      setAddError('Please enter a channel ID or handle');
      return;
    }

    setAdding(true);
    setAddError(null);

    try {
      // Determine if input is a handle or channel ID
      const isHandle = channelInput.startsWith('@');

      const newChannel = await addChannel({
        [isHandle ? 'handle' : 'channelId']: channelInput,
        rating,
      });

      // Reset form
      setChannelInput('');
      setRating(3);

      // Show success message
      setSuccessMessage(
        `Channel "${newChannel.title}" added! Fetching videos in the background...`
      );
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add channel');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateRating = async (channelId: string, newRating: number) => {
    try {
      await updateChannel(channelId, newRating);
    } catch (err) {
      console.error('Failed to update rating:', err);
    }
  };

  const handleDelete = async (channelId: string) => {
    if (!confirm('Are you sure you want to delete this channel?')) {
      return;
    }

    try {
      await deleteChannel(channelId);
    } catch (err) {
      console.error('Failed to delete channel:', err);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Channel Management
      </Typography>

      {/* Add Channel Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Channel
        </Typography>

        {addError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddError(null)}>
            {addError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label="Channel ID or @handle"
            placeholder="UCxxxxxx or @username"
            value={channelInput}
            onChange={(e) => setChannelInput(e.target.value)}
            fullWidth
            disabled={adding}
            helperText="Enter a YouTube channel ID (UCxxxxxx) or handle (@username)"
          />

          <Box sx={{ minWidth: 200 }}>
            <RatingSelector value={rating} onChange={setRating} />
          </Box>

          <Button
            variant="contained"
            onClick={handleAddChannel}
            disabled={adding || !channelInput.trim()}
            sx={{ minWidth: 120, height: 56 }}
          >
            {adding ? <CircularProgress size={24} /> : 'Add Channel'}
          </Button>
        </Box>
      </Paper>

      {/* Channels List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your Channels ({channels.length})
        </Typography>

        {loading && channels.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : channels.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No channels added yet. Add your first channel above!
          </Typography>
        ) : (
          <List>
            {channels.map((channel, index) => (
              <Box key={channel.id}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDelete(channel.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={channel.thumbnail} alt={channel.title} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={channel.title}
                    secondary={channel.customUrl || channel.id}
                  />
                  <Box sx={{ mr: 2 }}>
                    <RatingSelector
                      value={channel.rating}
                      onChange={(newRating) => handleUpdateRating(channel.id, newRating)}
                      showLabel={false}
                    />
                  </Box>
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  );
}
